<template>
  <div class="manage-script-markers-root">
  <md-dialog ref="manageDialog" class="manage-script-markers-dialog">
    <md-dialog-title>Manage structure</md-dialog-title>
    <md-dialog-content>
      <p v-if="!sortedMarkers.length" class="marker-empty-hint">No act, scene, or song structure marks yet.</p>
      <md-list v-else class="marker-list">
        <md-list-item v-for="marker in sortedMarkers" :key="marker.markerId || marker.id">
          <div class="marker-row">
            <span class="marker-type" :class="`marker-type-${marker.type}`">{{ typeLabel(marker.type) }}</span>
            <span class="marker-label">{{ marker.label }}</span>
            <span class="marker-page">{{ pageLabel(marker.pageIndex) }}</span>
            <span class="marker-actions">
              <md-button class="md-icon-button md-dense" @click="editMarker(marker)">
                <md-icon>edit</md-icon>
                <md-tooltip>Edit</md-tooltip>
              </md-button>
              <md-button class="md-icon-button md-dense" @click="confirmDelete(marker)">
                <md-icon>delete</md-icon>
                <md-tooltip>Delete</md-tooltip>
              </md-button>
            </span>
          </div>
        </md-list-item>
      </md-list>
    </md-dialog-content>
    <md-dialog-actions>
      <md-button class="md-primary" @click="openAdd">Add structure mark</md-button>
      <md-button @click="closeDialog">Close</md-button>
    </md-dialog-actions>
  </md-dialog>
  <script-marker-dialog
    ref="markerDialog"
    :currentPage="currentPage"
    :pageWidth="pageWidth"
    :pageHeight="pageHeight"
    :totalPages="totalPages"
    @saved="onMarkerSaved"
  />
  <md-dialog-confirm
    md-title="Delete marker"
    :md-content-html="deleteConfirmHtml"
    md-ok-text="Delete"
    md-cancel-text="Cancel"
    @close="onDeleteConfirm"
    ref="deleteDialog"
  />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { escapeHtml } from '@/lib/escapeHtml';
import ScriptMarkerDialog from '@/components/project/markers/marker-dialog';

export default {
    name: 'ManageScriptMarkersDialog',
    components: {
        'script-marker-dialog': ScriptMarkerDialog
    },
    props: {
        currentPage: { type: Number, required: true },
        pageWidth: { type: Number, default: 0 },
        pageHeight: { type: Number, default: 0 },
        totalPages: { type: Number, default: 0 }
    },
    data() {
        return {
            markerToDelete: null
        };
    },
    computed: {
        ...mapGetters(['scriptMarkers', 'project']),
        /**
         * Same fix as script-marker.vue, same reason: md-dialog-confirm throws
         * from mounted() when its content is falsy, and this dialog is rendered
         * inline. Derived from markerToDelete, which confirmDelete() already
         * sets, so it is correct by the time the dialog opens and is never
         * empty before then. Label escaped — raw-HTML sink, user-typed value.
         */
        deleteConfirmHtml() {
            const m = this.markerToDelete;
            if (!m || !m.label) return 'Delete this marker?';
            return `Delete <strong>${escapeHtml(m.label)}</strong> (${this.typeLabel(m.type).toLowerCase()})?`;
        },
        sortedMarkers() {
            // Works in version view too (corrected 23 Aug 2026). An earlier comment
            // here claimed these rows would write to the LIVE project and so had to
            // be hidden for a version. That was wrong: `projectPath` is
            // version-aware, so deleteScriptMarker / updateScriptMarker already
            // write to whichever document is open — and `scriptMarkers` now reads
            // from the same place. Editing a version is a supported workflow, so
            // structure behaves like cues, notes and layers instead of being the one
            // thing that reports "no structure" for a version that visibly has some.
            return [...(this.scriptMarkers || [])].sort((a, b) => {
                if (a.pageIndex !== b.pageIndex) {
                    return a.pageIndex - b.pageIndex;
                }
                if ((a.y || 0) !== (b.y || 0)) {
                    return (a.y || 0) - (b.y || 0);
                }
                return (a.x || 0) - (b.x || 0);
            });
        }
    },
    methods: {
        ...mapActions(['deleteScriptMarker']),
        typeLabel(type) {
            if (type === 'act') return 'Act';
            if (type === 'song') return 'Song';
            return 'Scene';
        },
        pageLabel(pageNum) {
            const pages = this.project && this.project.pdfPageNumbers;
            if (pages && pages[pageNum - 1] && pages[pageNum - 1].name) {
                return `Page ${pages[pageNum - 1].name}`;
            }
            return `Page ${pageNum}`;
        },
        open() {
            this.$refs.manageDialog.open();
        },
        closeDialog() {
            this.$refs.manageDialog.close();
        },
        openAdd() {
            this.$refs.markerDialog.openAdd();
        },
        editMarker(marker) {
            this.$refs.markerDialog.openEdit(marker);
        },
        onMarkerSaved() {
            this.$emit('markerSaved');
        },
        confirmDelete(marker) {
            this.markerToDelete = marker;
            this.$refs.deleteDialog.open();
        },
        async onDeleteConfirm(type) {
            if (type !== 'ok' || !this.markerToDelete) {
                this.markerToDelete = null;
                return;
            }
            try {
                await this.deleteScriptMarker(this.markerToDelete);
            } catch (err) {
                // Conflict toast is handled in marker-actions.
            }
            this.markerToDelete = null;
            this.$emit('markerDeleted');
        }
    }
};
</script>

<style scoped>
.marker-empty-hint {
  font-size: 13px;
  color: #666;
  margin: 8px 0;
}
.marker-list {
  padding: 0;
}
.marker-row {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
}
.marker-type {
  flex: 0 0 52px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #fff;
  border-radius: 3px;
  padding: 2px 6px;
  text-align: center;
}
.marker-type-act {
  background: #5A7298;
}
.marker-type-scene {
  background: #6589AB;
}
.marker-type-song {
  background: #CAD5E1;
  color: #2c3e50;
}
.marker-label {
  flex: 1 1 auto;
  font-weight: 500;
}
.marker-page {
  flex: 0 0 80px;
  font-size: 12px;
  color: #666;
}
.marker-actions {
  flex: 0 0 auto;
  display: flex;
}
</style>


// WEBPACK FOOTER //
// manage-markers-dialog.vue?6422edb2